<script setup lang="ts">
/**
 * AIAssistantProcessingView.vue - Processing State Wrapper
 *
 * Simple centered modal that displays during the processing state.
 * Shows a spinner and "Gerando notas..." message while the transcription
 * is being processed into a summary.
 *
 * This is a thin wrapper that reuses AIAssistantStartTranscription internally.
 */
import AIAssistantStartTranscription from "./AIAssistantStartTranscription.vue";

defineProps({
  sessionData: {
    type: Object,
    default: null,
  },
  isPollingActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["minimize"]);

const onMinimize = () => {
  emit("minimize");
};
</script>

<template>
  <div class="processing-view-backdrop">
    <div class="processing-view-modal">
      <div class="processing-header">
        <div class="header-title">
          <w-icon name="smart" size="large"></w-icon>
          <span class="processing-title">NOA Notes</span>
        </div>
        <w-icon-button class="icon-actions" icon-name="minus" type="button" @click="onMinimize" title="Minimizar" />
      </div>
      <div class="processing-content">
        <AIAssistantStartTranscription
          :summary-status="'processing'"
          :generating-transcription="true"
          :initial-context="sessionData"
        />
      </div>

      <!-- Polling indicator (shown when WebSocket is unavailable) -->
      <div v-if="isPollingActive" class="polling-indicator">
        <span class="status-dot"></span>
        <span class="status-text">Processando...</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// .processing-view-backdrop {
//   position: fixed;
//   inset: 0;
//   background: rgba(0, 0, 0, 0.1);
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   z-index: 9999999;
// }

.processing-view-modal {
  background: var(--w-modal-color-background);
  border-radius: 16px;
  width: 420px;
  box-shadow: var(--w-modal-box-shadow);
  overflow: hidden;
}

.processing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--w-border-color-default, #e0e0e0);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 8px;

  .w-icon,
  ::v-deep(.w-icon) {
    fill: #03c3a5;
  }
}

.processing-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--w-text-color-primary);
}

.icon-actions::v-deep(.w-icon) {
  fill: #565f5f;
}

.processing-content {
  padding: 24px;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Polling indicator - shown when WebSocket is unavailable */
.polling-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #22c55e;
  animation: blink 1.2s ease-in-out infinite;
}

.status-text {
  font-size: 11px;
  color: var(--w-text-color-tertiary, #6b7280);
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>
